import { axiosInstanceNew } from 'src/utils/axios';
import { handleContractError } from 'src/utils/crypto/handleContractError';

// Exchange-payout is a fiat-in (USD) -> crypto-out (USDT) swap whose destination
// is an exchange deposit address. It reuses the existing /swaps/fiat-intent
// route (same shape the Send-USD flow uses): tokenIn 'dollar', crypto payout to
// a recipient address, reconciled against the Sahal ledger before any USDT is
// sent. The server re-quotes the payout amount, so amountOut here is only what
// the user was shown.

// /swaps/fiat-intent only accepts these slippage values.
const ALLOWED_SLIPPAGE = [0.5, 2.5, 5];
const DEFAULT_SLIPPAGE = 0.5;

function coerceAllowedSlippage(value) {
  const n = Number(value);
  if (ALLOWED_SLIPPAGE.includes(n)) return n;
  return DEFAULT_SLIPPAGE;
}

// Build EXACTLY the fields createFiatIntentSchema declares — nothing more.
//
// The schema rejects unknown keys (Joi default), so we must NOT spread the raw
// exchange-payout payload: fields like amountUsd / payoutUsdt / tokenOut-as-extra
// are not in the schema and trigger '"amountUsd" is not allowed'. We map the
// exchange-payout fields onto the schema's field names and send only those:
//   amountUsd  -> amountIn   (tokenIn 'dollar')
//   payoutUsdt -> amountOut  (display only; server re-quotes the real amount)
//   chainId    -> sourceChainId / destChain
function toFiatIntentPayload(payload = {}) {
  const recipientAddress = String(
    payload.recipientAddress || payload.to || payload.address || ''
  ).trim();

  const chainId = payload.chainId ?? payload.sourceChainId ?? payload.destChain ?? null;

  const amountIn = String(payload.amountUsd ?? payload.amountIn ?? '').trim();
  const amountOut = String(payload.payoutUsdt ?? payload.amountOut ?? '').trim();

  return {
    // Optional idempotency hint the schema allows.
    clientRequestId: payload.clientRequestId,

    // Fiat-in side.
    tokenIn: 'dollar',
    amountIn,

    // Crypto-out side (display value; server recomputes the payout).
    tokenOut: String(payload.tokenOut || 'tether').trim(),
    amountOut,

    // Destination: the exchange deposit address. Schema requires one of
    // to / address / recipientAddress — populate all three with the same value.
    to: recipientAddress,
    address: recipientAddress,
    recipientAddress,

    // Chain: schema requires one of sourceChainId / destChain.
    sourceChainId: chainId,
    destChain: chainId,

    // Required by the schema.
    phoneNumber: String(payload.phoneNumber || '').trim(),
    networkFee: String(payload.networkFee ?? '0'),
    slippage: coerceAllowedSlippage(payload.slippage),
  };
}

export async function createExchangePayoutIntent(payload) {
  try {
    const fiatIntentPayload = toFiatIntentPayload(payload);

    const { data } = await axiosInstanceNew.post('/swaps/fiat-intent', fiatIntentPayload);

    return {
      success: Boolean(data?.success || data?.ok),
      ...data,
    };
  } catch (err) {
    return {
      success: false,
      code: err?.response?.data?.code || null,
      status: err?.response?.status || null,
      message:
        err?.response?.data?.message || err?.response?.data?.error || handleContractError(err),
      error: err,
    };
  }
}
